<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/topics/${topicId}/lessons`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Add Lesson</h6>
        <small class="text-muted" v-if="topic">for {{ topic.name }}</small>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="row">
                <div class="col-md-8 mb-3">
                  <label class="form-label">Lesson Title <span class="text-danger">*</span></label>
                  <input v-model="form.title" type="text" class="form-control" required placeholder="e.g., Introduction to Ancient India" />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Duration (minutes)</label>
                  <input v-model.number="form.duration" type="number" class="form-control" min="1" placeholder="30" />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Content</label>
                <textarea v-model="form.content" class="form-control" rows="6" placeholder="Lesson content (supports HTML)"></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label">Video URL (optional)</label>
                <input v-model="form.videoUrl" type="url" class="form-control" placeholder="https://youtube.com/..." />
              </div>

              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">Display Order</label>
                  <input v-model.number="form.order" type="number" class="form-control" min="1" />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Status</label>
                  <select v-model="form.status" class="form-select">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Create Lesson
                </button>
                <router-link :to="`/app/topics/${topicId}/lessons`" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'AddLesson',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    const topicId = route.params.topicId

    const topic = ref(null)
    const form = ref({
      title: '',
      content: '',
      videoUrl: '',
      duration: null,
      order: 1,
      status: 'draft'
    })
    const loading = ref(false)
    const error = ref('')

    const breadcrumbs = computed(() => [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: topic.value?.name || 'Topic', url: `/app/topics/${topicId}/lessons` },
      { title: 'Add Lesson', active: true }
    ])

    const fetchTopic = async () => {
      try {
        const response = await api.get(`/v1/admin/topics/${topicId}`)
        topic.value = response.data.topic
      } catch (err) {
        console.error('Failed to fetch topic:', err)
      }
    }

    const handleSubmit = async () => {
      loading.value = true
      error.value = ''

      try {
        await api.post(`/v1/admin/topics/${topicId}/lessons`, form.value)
        alert.success('Success', 'Lesson created successfully')
        router.push(`/app/topics/${topicId}/lessons`)
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to create lesson'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchTopic)

    return { topicId, topic, form, loading, error, breadcrumbs, handleSubmit }
  }
}
</script>
